import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, RotateCcw, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useListState } from '../../../shared/hooks';
import { FormInput } from '../../../shared/FormInput';
import { FormSelect } from '../../../shared/FormSelect';
import type { BaseListFilters, SortDirection } from '../../../shared/types/list.types';
import { csvFileDate, downloadCsv } from '@/utils/csv';
import {
  fetchContractsPaymentSummary,
  useSocietyContracts,
  type ContractListItem,
} from '../hooks/useContractsPayments';
import { CONTRACT_SUMMARY_CSV_COLUMNS } from '../utils/contractDetailFormat';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { contractApi } from '@/features/contracts/api/contractApi';
import {
  MANDATE_AVAILABLE_STATUSES,
  getPaymentStatusInfo,
} from '@/features/contracts/constants/payment-status';
import { ContractDetailModal } from './ContractDetailModal';
import { FilterPopover } from './FilterPopover';

interface ContractsFilters extends BaseListFilters {
  searchTerm?: string;
  /** 'stripe' (défaut) | 'monthly' | 'online' */
  paymentType?: string;
  status?: string;
  /** Nombre de jours (préset Période) → converti en creationDateFrom côté requête. */
  datePreset?: string;
  minAmount?: string;
  maxAmount?: string;
}

const STATUS_OPTIONS = [
  { value: '', label: 'Tous les statuts' },
  { value: 'active', label: 'Actif' },
  { value: 'payment_ongoing', label: 'Paiement en cours' },
  { value: 'payment_failed', label: 'Paiement échoué' },
  { value: 'cancelled', label: 'Annulé' },
  { value: 'expired', label: 'Expiré' },
];
const MODE_OPTIONS = [
  { value: 'stripe', label: 'Tous' },
  { value: 'monthly', label: 'Mensuel' },
  { value: 'online', label: 'Comptant en ligne' },
];
const PERIOD_OPTIONS = [
  { value: '', label: 'Toutes' },
  { value: '7', label: '7 derniers jours' },
  { value: '30', label: '30 derniers jours' },
  { value: '90', label: '90 derniers jours' },
  { value: '365', label: '12 derniers mois' },
];
const labelOf = (opts: { value: string; label: string }[], v?: string) =>
  opts.find((o) => o.value === v)?.label;

/** Mode de paiement dérivé du préfixe de monthlyPaymentId (pas de champ stocké). */
function getPaymentMode(monthlyPaymentId: string | null): {
  label: string;
  hasStripePayment: boolean;
} {
  if (!monthlyPaymentId) return { label: 'Gratuit / comptant agence', hasStripePayment: false };
  if (monthlyPaymentId.startsWith('sub_') || monthlyPaymentId.startsWith('seti_')) {
    return { label: 'Mensuel', hasStripePayment: true };
  }
  if (monthlyPaymentId.startsWith('pi_')) {
    return { label: 'Comptant en ligne', hasStripePayment: true };
  }
  return { label: 'Autre', hasStripePayment: true };
}

/** « 499,00 EUR » (colonne Montant, format Stripe — code devise, sans symbole €). */
const euroEur = (n: number | null | undefined) =>
  n == null
    ? '—'
    : `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;

const amountPill = (min?: string, max?: string) => {
  if (min && max) return `${min}–${max} €`;
  if (min) return `≥ ${min} €`;
  if (max) return `≤ ${max} €`;
  return null;
};

/** Pastille de statut contrat, style « fintech » (comme les listes Stripe). */
const StatusPill: React.FC<{ status: number }> = ({ status }) => {
  const map: Record<number, { label: string; cls: string }> = {
    1: { label: 'Actif', cls: 'tw-bg-green-50 tw-text-green-700' },
    2: { label: 'Expiré', cls: 'tw-bg-gray-100 tw-text-gray-600' },
    3: { label: 'Annulé', cls: 'tw-bg-red-50 tw-text-red-700' },
    4: { label: 'Suspendu', cls: 'tw-bg-amber-50 tw-text-amber-700' },
  };
  const s = map[status] ?? { label: String(status), cls: 'tw-bg-gray-100 tw-text-gray-600' };
  return (
    <span
      className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium ${s.cls}`}
    >
      {s.label}
    </span>
  );
};

/** Pastille de l'état du dernier paiement (MONTHLY_PAYMENT_STATUS). */
const PaymentStatusPill: React.FC<{ status: string | null }> = ({ status }) => {
  if (!status) return <span className="tw-text-gray-400">—</span>;
  const info = getPaymentStatusInfo(status);
  return (
    <span
      className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium ${info.color}`}
    >
      {info.label}
    </span>
  );
};

/**
 * Onglet « Contrats & paiements » : liste paginée + filtrée des contrats ayant un
 * paiement Stripe, au style des listes Stripe embarquées (pastilles compactes,
 * entêtes gris, montants alignés, rendu « flush », pager « X-Y sur N »). Pagination /
 * filtres / tri côté serveur (contrats-v2) via useListState.
 */
export const ContractsPaymentsTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const {
    filters,
    updateFilter,
    pagination,
    setPagination,
    sortConfig,
    setSortConfig,
    resetFilters,
    activeFiltersCount,
  } = useListState<ContractsFilters>(
    { defaultLimit: 10, defaultSortBy: 'dateCreation', defaultSortDirection: 'DESC' },
    { paymentType: 'stripe' },
  );

  // Brouillons édités dans les popovers, committés au clic « Appliquer ».
  const [searchDraft, setSearchDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState('');
  const [modeDraft, setModeDraft] = useState('stripe');
  const [periodDraft, setPeriodDraft] = useState('');
  const [minDraft, setMinDraft] = useState('');
  const [maxDraft, setMaxDraft] = useState('');

  // Préset Période → date de création minimale (calcul client, pas de workflow ici).
  const creationDateFrom = useMemo(() => {
    const days = Number(filters.datePreset);
    if (!filters.datePreset || Number.isNaN(days)) return undefined;
    return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
  }, [filters.datePreset]);

  // Critères de la liste, repris tels quels par l'export.
  const criteria = {
    sortBy: sortConfig.sortBy,
    sortDirection: sortConfig.sortDirection,
    searchTerm: filters.searchTerm,
    paymentType: filters.paymentType,
    status: filters.status,
    creationDateFrom,
    minAmount: filters.minAmount,
    maxAmount: filters.maxAmount,
  };

  const { data, isLoading, error } = useSocietyContracts(societyId, {
    page: pagination.page,
    limit: pagination.limit,
    ...criteria,
  });

  // Export : la fiche de chaque contrat filtré (toutes pages), sans ses paiements.
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = await fetchContractsPaymentSummary(societyId, criteria);
      if (rows.length === 0) {
        toast('Aucun contrat à exporter.');
        return;
      }
      downloadCsv(rows, CONTRACT_SUMMARY_CSV_COLUMNS, `contrats-${csvFileDate()}.csv`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Export impossible.');
    } finally {
      setExporting(false);
    }
  };

  const contracts = useMemo(() => data?.contracts ?? [], [data]);
  const total = data?.total ?? 0;

  const [selected, setSelected] = useState<ContractListItem | null>(null);

  // Option vendeur « mandat de prélèvement client » → affiche la colonne Document.
  // Config effective de la société affichée (déjà en cache : self ET admin).
  const { data: societyConfig } = useEffectiveConfiguration(societyId);
  const showMandateColumn = !!societyConfig?.clientMandateDownload;

  // Téléchargement du mandat SEPA d'un contrat (spinner sur la ligne en cours).
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const handleDownloadMandate = async (e: React.MouseEvent, c: ContractListItem) => {
    e.stopPropagation(); // ne pas ouvrir la modale détail
    setDownloadingId(c.id);
    try {
      const blob = await contractApi.downloadSepaMandate(c.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mandat-sepa-${c.label || c.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || 'Impossible de télécharger le mandat SEPA',
      );
    } finally {
      setDownloadingId(null);
    }
  };

  // Mandat téléchargeable : contrat mensualisé (sub_/seti_) + statut éligible.
  const isMandateEligible = (c: ContractListItem) =>
    !!c.monthlyPaymentId &&
    (c.monthlyPaymentId.startsWith('sub_') || c.monthlyPaymentId.startsWith('seti_')) &&
    !!c.monthlyPaymentStatus &&
    MANDATE_AVAILABLE_STATUSES.has(c.monthlyPaymentStatus);

  const handleReset = () => {
    resetFilters();
    setSearchDraft('');
    setStatusDraft('');
    setModeDraft('stripe');
    setPeriodDraft('');
    setMinDraft('');
    setMaxDraft('');
  };

  // Entête de colonne (triable ou non), style clair Stripe-like : gris moyen, medium.
  const header = (label: string, opts?: { sortKey?: string; align?: 'right' }) => {
    const align = opts?.align === 'right' ? 'tw-text-right' : 'tw-text-left';
    const base = `tw-px-4 tw-py-3 tw-text-[13px] tw-font-medium tw-text-gray-500 ${align}`;
    if (!opts?.sortKey) return <th className={base}>{label}</th>;
    const sortKey = opts.sortKey;
    const activeSort = sortConfig.sortBy === sortKey;
    const dir = activeSort ? sortConfig.sortDirection : null;
    return (
      <th
        className={`${base} tw-cursor-pointer tw-select-none hover:tw-text-gray-700`}
        onClick={() => {
          const next: SortDirection = activeSort && dir === 'DESC' ? 'ASC' : 'DESC';
          setSortConfig({ sortBy: sortKey, sortDirection: next });
        }}
      >
        <span
          className={`tw-inline-flex tw-items-center tw-gap-1 ${opts.align === 'right' ? 'tw-flex-row-reverse' : ''}`}
        >
          {label}
          {activeSort &&
            (dir === 'ASC' ? (
              <ChevronUp className="tw-w-3 tw-h-3" />
            ) : (
              <ChevronDown className="tw-w-3 tw-h-3" />
            ))}
        </span>
      </th>
    );
  };

  const COLSPAN = 6 + (showMandateColumn ? 1 : 0);
  const from = total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const to = Math.min(pagination.page * pagination.limit, total);
  const canPrev = pagination.page > 1;
  const canNext = pagination.page * pagination.limit < total;

  const numberInputCls =
    'tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-2 tw-py-1 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20';
  const pagerBtnCls =
    'tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-lg tw-border tw-border-gray-200 tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed';

  return (
    <>
      <div className="tw-bg-white">
        {/* Barre de filtres façon Stripe (pastilles + popovers) */}
        <div className="tw-p-3 tw-border-b tw-border-gray-200 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <FilterPopover
            label="Recherche"
            active={!!filters.searchTerm}
            pillValue={filters.searchTerm}
            onOpen={() => setSearchDraft(filters.searchTerm ?? '')}
            onApply={() => updateFilter('searchTerm', searchDraft || undefined)}
            onClear={() => updateFilter('searchTerm', undefined)}
          >
            <FormInput
              size="sm"
              value={searchDraft}
              onChange={setSearchDraft}
              placeholder="N°, bénéficiaire, e-mail..."
            />
          </FilterPopover>

          <FilterPopover
            label="État"
            active={!!filters.status}
            pillValue={labelOf(STATUS_OPTIONS, filters.status)}
            onOpen={() => setStatusDraft(filters.status ?? '')}
            onApply={() => updateFilter('status', statusDraft || undefined)}
            onClear={() => updateFilter('status', undefined)}
          >
            <FormSelect size="sm" value={statusDraft} onChange={setStatusDraft} options={STATUS_OPTIONS} />
          </FilterPopover>

          <FilterPopover
            label="Moyen de paiement"
            active={!!filters.paymentType && filters.paymentType !== 'stripe'}
            pillValue={labelOf(MODE_OPTIONS, filters.paymentType)}
            onOpen={() => setModeDraft(filters.paymentType ?? 'stripe')}
            onApply={() => updateFilter('paymentType', modeDraft || 'stripe')}
            onClear={() => updateFilter('paymentType', 'stripe')}
          >
            <FormSelect size="sm" value={modeDraft} onChange={setModeDraft} options={MODE_OPTIONS} />
          </FilterPopover>

          <FilterPopover
            label="Période"
            active={!!filters.datePreset}
            pillValue={labelOf(PERIOD_OPTIONS, filters.datePreset)}
            onOpen={() => setPeriodDraft(filters.datePreset ?? '')}
            onApply={() => updateFilter('datePreset', periodDraft || undefined)}
            onClear={() => updateFilter('datePreset', undefined)}
          >
            <FormSelect size="sm" value={periodDraft} onChange={setPeriodDraft} options={PERIOD_OPTIONS} />
          </FilterPopover>

          <FilterPopover
            label="Montant"
            active={!!filters.minAmount || !!filters.maxAmount}
            pillValue={amountPill(filters.minAmount, filters.maxAmount)}
            onOpen={() => {
              setMinDraft(filters.minAmount ?? '');
              setMaxDraft(filters.maxAmount ?? '');
            }}
            onApply={() => {
              updateFilter('minAmount', minDraft || undefined);
              updateFilter('maxAmount', maxDraft || undefined);
            }}
            onClear={() => {
              updateFilter('minAmount', undefined);
              updateFilter('maxAmount', undefined);
            }}
          >
            <div className="tw-flex tw-items-center tw-gap-2">
              <input
                type="number"
                value={minDraft}
                onChange={(e) => setMinDraft(e.target.value)}
                placeholder="Min €"
                className={numberInputCls}
              />
              <span className="tw-text-gray-400">—</span>
              <input
                type="number"
                value={maxDraft}
                onChange={(e) => setMaxDraft(e.target.value)}
                placeholder="Max €"
                className={numberInputCls}
              />
            </div>
          </FilterPopover>

          {activeFiltersCount > 0 && (
            <button
              type="button"
              onClick={handleReset}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-text-gray-500 hover:tw-text-gray-700"
            >
              <RotateCcw className="tw-w-4 tw-h-4" />
              Réinitialiser
            </button>
          )}

          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || total === 0}
            className="tw-ml-auto tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-gray-100 tw-text-gray-800 hover:tw-bg-gray-200 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {exporting ? (
              <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
            ) : (
              <Download className="tw-w-4 tw-h-4" />
            )}
            Exporter
          </button>
        </div>

        {/* Table */}
        <div className="tw-overflow-x-auto">
          <table className="tw-min-w-full">
            <thead>
              <tr className="tw-border-b tw-border-gray-200">
                {header('Contrat', { sortKey: 'label' })}
                {header('Bénéficiaire', { sortKey: 'beneficiary' })}
                {header('Mode')}
                {header('Montant', { align: 'right' })}
                {header('Statut', { sortKey: 'idStatus' })}
                {header('Dernier paiement')}
                {showMandateColumn && header('Document')}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={COLSPAN} className="tw-px-4 tw-py-10 tw-text-center tw-text-gray-500">
                    Chargement…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={COLSPAN} className="tw-px-4 tw-py-10 tw-text-center tw-text-red-600">
                    Erreur de chargement des contrats.
                  </td>
                </tr>
              ) : contracts.length === 0 ? (
                <tr>
                  <td colSpan={COLSPAN} className="tw-px-4 tw-py-10 tw-text-center tw-text-gray-500">
                    Aucun contrat avec paiement Stripe.
                  </td>
                </tr>
              ) : (
                contracts.map((c) => {
                  const mode = getPaymentMode(c.monthlyPaymentId);
                  const beneficiary = c.beneficiary
                    ? `${c.beneficiary.firstName ?? ''} ${c.beneficiary.lastName ?? ''}`.trim()
                    : '';
                  const amount =
                    c.monthlyPriceClient != null
                      ? `${euroEur(c.monthlyPriceClient)} /mois`
                      : euroEur(c.sellingPrice);
                  return (
                    <tr
                      key={c.id}
                      onClick={() => setSelected(c)}
                      className="tw-border-b tw-border-gray-100 last:tw-border-0 hover:tw-bg-gray-50 tw-cursor-pointer tw-transition-colors"
                    >
                      <td className="tw-px-4 tw-py-2.5 tw-text-sm tw-text-gray-900">{c.label}</td>
                      <td className="tw-px-4 tw-py-2.5 tw-text-sm tw-text-gray-700">
                        {beneficiary || '—'}
                      </td>
                      <td className="tw-px-4 tw-py-2.5">
                        <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-xs tw-text-gray-600">
                          {mode.label}
                        </span>
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-text-sm tw-text-right tw-tabular-nums tw-font-semibold tw-text-gray-900">
                        {amount}
                      </td>
                      <td className="tw-px-4 tw-py-2.5">
                        <StatusPill status={c.idStatus} />
                      </td>
                      <td className="tw-px-4 tw-py-2.5">
                        <PaymentStatusPill status={c.monthlyPaymentStatus} />
                      </td>
                      {showMandateColumn && (
                        <td className="tw-px-4 tw-py-2.5">
                          {isMandateEligible(c) ? (
                            <button
                              type="button"
                              onClick={(e) => handleDownloadMandate(e, c)}
                              disabled={downloadingId === c.id}
                              title="Télécharger le mandat SEPA"
                              className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-text-affinicar-primary hover:tw-text-affinicar-secondary disabled:tw-opacity-50"
                            >
                              {downloadingId === c.id ? (
                                <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                              ) : (
                                <Download className="tw-w-4 tw-h-4" />
                              )}
                              Mandat SEPA
                            </button>
                          ) : (
                            <span className="tw-text-gray-400">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination façon Stripe : « X-Y sur N » + Précédent / Suivant */}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-border-t tw-border-gray-200">
          <span className="tw-text-sm tw-text-gray-500">
            {total === 0 ? '0 résultat' : `${from}-${to} sur ${total} résultats`}
          </span>
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              type="button"
              disabled={!canPrev}
              onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
              className={pagerBtnCls}
            >
              Précédent
            </button>
            <button
              type="button"
              disabled={!canNext}
              onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              className={pagerBtnCls}
            >
              Suivant
            </button>
          </div>
        </div>
      </div>

      {selected && (
        <ContractDetailModal contract={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
};
